import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { periodWindowLabel } from 'components/IncidentConfig/roster/rosterPeriods';
import apiConfig from 'constants/apiConfig';
import { useActivationActions } from 'hooks/incident/useActivationActions';
import { keys as rosterKeys } from 'hooks/incident/useRoster';
import {
  invalidatePlannedPeriodQueries,
  usePlannedOperationalPeriods,
} from 'hooks/incident/usePlannedOperationalPeriods';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';

type PeriodOption = {
  periodNumber: number;
  current: boolean;
  window: string;
};

/**
 * Accept or decline an HWCG activation request from the in-app notification.
 * Decline stays disabled until Notes has text. Accept Activation opens Incident
 * Profile. Roster requests let the person pick the first operational period
 * they can work, from the one they were asked about onward.
 */
export function HwcgActivationReply({
  notificationId,
  incidentId,
  rosterActivation = false,
  requestPeriodNumber = null,
}: {
  notificationId: string;
  incidentId: string;
  /** The request is for a roster seat, possibly in a later operational period. */
  rosterActivation?: boolean;
  /** The operational period the request is for. */
  requestPeriodNumber?: number | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userGuid = useAppSelector(selectUserGuid);
  const hwcg = useHwcgFlag();
  const { mutateAsync: updateActivation } = useActivationActions();
  const [note, setNote] = useState('');
  const [chosenPeriod, setChosenPeriod] = useState<number | null>(null);
  const noteText = note.trim();

  const pickPeriod = hwcg && rosterActivation;
  const { view } = usePlannedOperationalPeriods(incidentId, pickPeriod);
  const options: PeriodOption[] = [
    ...(view.current
      ? [
          {
            periodNumber: view.current.periodNumber,
            current: true,
            window: periodWindowLabel(
              view.current.startDateTime,
              view.current.anticipatedEndDateTime
            ),
          },
        ]
      : []),
    ...view.periods.map((period) => ({
      periodNumber: period.periodNumber,
      current: false,
      window: periodWindowLabel(
        period.startDateTime,
        period.anticipatedEndDateTime
      ),
    })),
  ];
  const requestedNumber = requestPeriodNumber ?? view.current?.periodNumber;
  const requested = options.find(
    (option) => option.periodNumber === requestedNumber
  );
  const selectedPeriod = chosenPeriod ?? requested?.periodNumber ?? null;
  const showPicker = pickPeriod && options.length > 0;

  const reply = useMutation({
    mutationFn: async (outcome: 'activated' | 'declined') => {
      const response = await axios.post<{
        currentPeriod?: boolean;
        scheduledPeriodNumber?: number;
      }>(apiConfig.apiGateway.hwcgActivationReply(incidentId), {
        notificationId,
        outcome,
        note: noteText || undefined,
        ...(showPicker && outcome === 'activated' && selectedPeriod != null
          ? { firstAvailablePeriodNumber: selectedPeriod }
          : {}),
      });
      return {
        currentPeriod: response.data?.currentPeriod !== false,
        scheduledPeriodNumber: response.data?.scheduledPeriodNumber,
      };
    },
    onError: () => {
      toast.error('The activation reply could not be saved.');
    },
    onSuccess: async ({ currentPeriod, scheduledPeriodNumber }, outcome) => {
      await queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
      await queryClient.invalidateQueries({
        queryKey: [rosterKeys.hwcgActivationLog, incidentId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['hwcg-group-actions', incidentId],
      });
      if (rosterActivation) {
        invalidatePlannedPeriodQueries(queryClient, incidentId);
      }
      // A later period's seat is answered on that seat; the incident membership
      // follows the current period only.
      if (outcome === 'activated' && !currentPeriod) {
        toast.success(
          scheduledPeriodNumber != null
            ? `Accepted. You are scheduled for Operational Period ${scheduledPeriodNumber}.`
            : 'Accepted. You are scheduled for that operational period.'
        );
        return;
      }
      if (outcome === 'activated' && userGuid) {
        try {
          await updateActivation({
            incidentId,
            memberUserGuid: userGuid,
            isActivated: true,
          });
        } catch {
          toast.warn(
            'Activation was recorded. Your incident membership could not be updated.'
          );
        }
        navigate(`/iap/${incidentId}/profile`);
      }
    },
  });

  return (
    <div className="tw:mt-2 tw:flex tw:max-w-xl tw:flex-col tw:gap-2">
      {showPicker ? (
        <div
          className="tw:flex tw:flex-col tw:gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          {requested ? (
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              This request is for Operational Period {requested.periodNumber}
              {requested.current ? ' (current)' : ' (scheduled)'}:{' '}
              {requested.window}
            </p>
          ) : null}
          <span className="tw:text-xs tw:font-medium">
            First operational period you can work
          </span>
          <RadioGroup
            aria-label="First operational period you can work"
            value={selectedPeriod != null ? String(selectedPeriod) : ''}
            disabled={reply.isPending}
            onValueChange={(next) => setChosenPeriod(Number(next))}
            className="tw:gap-1.5"
          >
            {options.map((option) => {
              const id = `hwcg-reply-period-${notificationId}-${option.periodNumber}`;
              const beforeRequest =
                requested != null &&
                option.periodNumber < requested.periodNumber;
              return (
                <div
                  key={option.periodNumber}
                  className="tw:flex tw:items-start tw:gap-2"
                >
                  <RadioGroupItem
                    value={String(option.periodNumber)}
                    id={id}
                    disabled={beforeRequest}
                    className="tw:mt-0.5"
                  />
                  <Label
                    htmlFor={id}
                    className="tw:flex tw:flex-col tw:items-start tw:gap-0 tw:text-xs tw:font-normal"
                  >
                    <span className="tw:font-medium">
                      Operational Period {option.periodNumber}{' '}
                      {option.current ? '(current)' : '(scheduled)'}
                    </span>
                    <span className="tw:text-muted-foreground">
                      {option.window}
                      {beforeRequest ? ' · Before this request' : ''}
                    </span>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      ) : null}
      <label className="tw:flex tw:flex-col tw:gap-1 tw:text-xs tw:font-medium">
        Notes
        <Textarea
          value={note}
          rows={3}
          placeholder="Required to decline activation"
          onChange={(event) => setNote(event.target.value)}
          onClick={(event) => event.stopPropagation()}
        />
      </label>
      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <Button
          type="button"
          size="sm"
          disabled={reply.isPending}
          onClick={(event) => {
            event.stopPropagation();
            reply.mutate('activated');
          }}
        >
          Accept Activation
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={reply.isPending || noteText.length === 0}
          onClick={(event) => {
            event.stopPropagation();
            reply.mutate('declined');
          }}
        >
          Decline Activation
        </Button>
      </div>
    </div>
  );
}
